import axios from 'axios'
import { useAuthStore } from '../store/authStore'
import { API_URL, authenticatedFetch, refreshAccessToken } from './authFetch'
import {
  AuthRequiredError,
  requestAuthGate,
  shouldBlockGuestWrite,
  shouldShowAuthGateForPath,
} from '../utils/authGate'

export { API_URL }

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - add auth token; block guest writes (modal only for generate/upload)
api.interceptors.request.use((config) => {
  const url = config.url || ''
  if (shouldBlockGuestWrite(config.method, url)) {
    const { isAuthenticated, accessToken } = useAuthStore.getState()
    if (!isAuthenticated || !accessToken) {
      if (shouldShowAuthGateForPath(url)) {
        requestAuthGate(url)
      }
      return Promise.reject(new AuthRequiredError())
    }
  }

  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      const refreshToken = useAuthStore.getState().refreshToken
      
      if (refreshToken) {
        const access_token = await refreshAccessToken()
        if (access_token) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          return api(originalRequest)
        }
        return Promise.reject(error)
      }
      const { isGuest } = useAuthStore.getState()
      if (isGuest) {
        return Promise.reject(error)
      }
      useAuthStore.getState().logout()
      window.location.href = '/login'
    }
    
    return Promise.reject(error)
  }
)

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
    
  register: (email: string, password: string, first_name?: string, last_name?: string, captcha_token?: string) =>
    api.post('/auth/register', { email, password, first_name, last_name, captcha_token }),
    
  verify: (token: string) =>
    api.post('/auth/verify', { token }),
    
  resendVerification: (email: string) =>
    api.post('/auth/verify/resend', { email }),
    
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),
    
  resetPassword: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, new_password: newPassword }),
    
  logout: () => {
    const refresh_token = useAuthStore.getState().refreshToken
    return api.post('/auth/logout', { refresh_token: refresh_token || '' })
  },
    
  getMe: () =>
    api.get('/auth/me'),

  vkConfig: () =>
    api.get<{ enabled: boolean; client_id?: string }>('/auth/vk/config'),

  vkStart: (payload: {
    mode?: 'login' | 'link'
    redirect_uri?: string
  }) =>
    api.post<{
      authorize_url: string
      state: string
      redirect_uri: string
    }>('/auth/vk/start', payload),

  vkExchange: (payload: {
    code: string
    device_id: string
    state: string
    redirect_uri?: string
  }) =>
    api.post<{
      access_token: string
      refresh_token: string
      token_type: string
      expires_in: number
      user: {
        id: string
        email: string
        first_name?: string
        last_name?: string
        credits_balance: number
        is_superadmin?: boolean
        is_active: boolean
        is_verified: boolean
        is_premium: boolean
        created_at: string
      }
      is_new_user: boolean
    }>('/auth/vk/exchange', payload),

  vkLink: (payload: {
    code: string
    device_id: string
    state: string
    redirect_uri?: string
  }) =>
    api.post<{
      user: {
        id: string
        email: string
        first_name?: string
        last_name?: string
        credits_balance: number
        is_superadmin?: boolean
        vk_linked?: boolean
        has_password?: boolean
      }
      message: string
    }>('/auth/vk/link', payload),

  vkUnlink: () =>
    api.delete<{
      user: {
        id: string
        email: string
        vk_linked?: boolean
        google_linked?: boolean
        has_password?: boolean
      }
      message: string
    }>('/auth/vk/link'),

  googleConfig: () =>
    api.get<{ enabled: boolean; client_id?: string }>('/auth/google/config'),

  googleStart: (payload: {
    mode?: 'login' | 'link'
    redirect_uri?: string
  }) =>
    api.post<{
      authorize_url: string
      state: string
      redirect_uri: string
    }>('/auth/google/start', payload),

  googleExchange: (payload: {
    code: string
    state: string
    redirect_uri?: string
    code_verifier?: string
  }) =>
    api.post<{
      access_token: string
      refresh_token: string
      token_type: string
      expires_in: number
      user: {
        id: string
        email: string
        first_name?: string
        last_name?: string
        credits_balance: number
        is_superadmin?: boolean
        is_active: boolean
        is_verified: boolean
        is_premium: boolean
        created_at: string
        vk_linked?: boolean
        google_linked?: boolean
        has_password?: boolean
      }
      is_new_user: boolean
    }>('/auth/google/exchange', payload),

  googleLink: (payload: {
    code: string
    state: string
    redirect_uri?: string
    code_verifier?: string
  }) =>
    api.post<{
      user: {
        id: string
        email: string
        first_name?: string
        last_name?: string
        credits_balance: number
        is_superadmin?: boolean
        vk_linked?: boolean
        google_linked?: boolean
        has_password?: boolean
      }
      message: string
    }>('/auth/google/link', payload),

  googleUnlink: () =>
    api.delete<{
      user: {
        id: string
        email: string
        vk_linked?: boolean
        google_linked?: boolean
        has_password?: boolean
      }
      message: string
    }>('/auth/google/link'),
}

// Generation API
export const generationApi = {
  uploadImage: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post('/upload/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },
  
  getJobs: (params?: { limit?: number; offset?: number; status?: string; chat_filter?: string; search?: string }) =>
    api.get('/jobs', { params }),

  getHistory: (params?: { limit?: number; offset?: number; search?: string; filter?: string }) =>
    api.get('/history', { params }),
    
  getJob: (id: string) =>
    api.get(`/jobs/${id}`),
    
  deleteJob: (id: string) =>
    api.delete(`/jobs/${id}`),
}

// Credits API
export const creditsApi = {
  getBalance: () =>
    api.get('/user/credits'),
    
  getStats: () =>
    api.get('/user/stats'),
    
  getPlans: () =>
    api.get('/plans'),
    
  getTransactions: (limit = 20, offset = 0) =>
    api.get('/user/transactions', { params: { limit, offset } }),

  getPricing: () =>
    api.get('/pricing'),

  getModelCatalog: (jobType: 'text-to-image' | 'image-to-image') =>
    api.get<{ models: import('../config/models').CatalogEntry[]; job_type: string }>(
      '/models/catalog',
      { params: { job_type: jobType } },
    ),
}

// Chats API
export const chatsApi = {
  getChats: (params?: { mode?: string; limit?: number; offset?: number }) =>
    api.get('/chats', { params }),
    
  createChat: (data: { title?: string; mode?: string }) =>
    api.post('/chats', data),
    
  getChat: (id: string) =>
    api.get(`/chats/${id}`),
    
  deleteChat: (id: string) =>
    api.delete(`/chats/${id}`),
    
  generateInChat: (chatId: string, data: {
    prompt: string
    aspect_ratio: string
    style: string
    model: string
    resolution?: string
    quality?: string
  }) => api.post(`/chats/${chatId}/generate`, data),
  
  generateImageToImage: (chatId: string, data: {
    prompt: string
    image_url?: string
    image_urls?: string[]
    source_job_id?: string
    aspect_ratio: string
    model: string
    resolution?: string
    strength?: number
    quality?: string
  }) => api.post(`/chats/${chatId}/generate-image`, data),

  removeBackground: (chatId: string, data: {
    image_url: string
    model?: string
    model_variant?: string
  }) => api.post(`/chats/${chatId}/remove-background`, data),
}

// Favorites API
export const favoritesApi = {
  add: (jobId: string) => api.post('/favorites', null, { params: { job_id: jobId } }),
  
  remove: (jobId: string) => api.delete(`/favorites/${jobId}`),
  
  list: (params?: { limit?: number; offset?: number; search?: string }) =>
    api.get('/favorites', { params }),
  
  status: (jobIds: string[]) =>
    api.get('/favorites/status', { params: { job_ids: jobIds.join(',') } }),
}

// Prompts Library API
export const promptsApi = {
  list: (params?: { category?: string; search?: string; page?: number; limit?: number; sort?: string }) =>
    api.get('/prompts', { params }),
    
  categories: () =>
    api.get('/prompts/categories'),
    
  get: (id: number) =>
    api.get(`/prompts/${id}`),
    
  use: (data: { prompt_id: number; variables?: Record<string, string>; model?: string; aspect_ratio?: string; language?: string }) =>
    api.post('/prompts/use', data),
    
  toggleFavorite: (promptId: number) =>
    api.post(`/prompts/favorites/${promptId}`),
    
  listFavorites: () =>
    api.get('/prompts/favorites/list'),
}
// ═════════ AI Chat ═════════
export const aiChatApi = {
  createChat: (assistantId?: string) =>
    api.post('/assistant/chats', { assistant_id: assistantId }),

  listChats: (params?: { limit?: number; offset?: number; assistant_id?: string }) =>
    api.get('/assistant/chats', { params }),

  getChat: (chatId: string) =>
    api.get(`/assistant/chats/${chatId}`),

  deleteChat: (chatId: string) =>
    api.delete(`/assistant/chats/${chatId}`),

  sendMessage: (chatId: string, content: string) =>
    api.post('/assistant/messages', { chat_id: chatId, content }),
}

export const paymentsApi = {
  initPayment: (amount: number) =>
    api.post('/payments/init', { amount }),

  getStatus: (orderId: string, options?: { cancelled?: boolean }) =>
    api.get<{
      order_id: string
      payment_id?: string
      amount_usd: number
      currency: string
      credits: number
      status: string
      payment_kind?: string
      plan_slug?: string | null
      created_at?: string
    }>(`/payments/status/${orderId}`, {
      params: options?.cancelled ? { cancelled: true } : undefined,
    }),

  getHistory: () =>
    api.get('/payments/history'),

  getExchangeRate: () =>
    api.get('/payments/exchange-rate'),
}

export interface SubscriptionPlanPublic {
  slug: string
  name: string
  price_usd: number
  currency: string
  subscription_credits_monthly: number
  ai_chat_message_limit: number | null
  features: Record<string, unknown>
  savings_pct?: number | null
  sort_order?: number
}

export interface SubscriptionStatus {
  has_premium_access: boolean
  trial_active: boolean
  trial_premium_ends_at: string | null
  purchased_balance: number
  subscription_balance: number
  total_available: number
  plan_slug: string | null
  plan_name: string | null
  period_end: string | null
  ai_chat: {
    limit: number | null
    used: number
    unlimited: boolean
  }
}

export interface SubscriptionErrorDetail {
  code: 'subscription_required' | 'ai_chat_limit_reached'
  feature?: string
  trial_expired?: boolean
  upgrade_url?: string
  limit?: number
  used?: number
  message?: string
}

export class SubscriptionAccessError extends Error {
  detail: SubscriptionErrorDetail

  constructor(detail: SubscriptionErrorDetail) {
    super(detail.message || 'Subscription required')
    this.name = 'SubscriptionAccessError'
    this.detail = detail
  }
}

export const subscriptionsApi = {
  getPlans: () =>
    api.get<{ free: Record<string, unknown>; plans: SubscriptionPlanPublic[] }>('/subscriptions/plans'),

  getMe: () =>
    api.get<SubscriptionStatus>('/subscriptions/me'),

  checkout: (plan_slug: string) =>
    api.post<{
      order_id: string
      payment_url: string
      amount_usd: number
      currency: string
      plan_slug: string
      plan_name: string
      subscription_credits: number
      payment_kind: string
    }>('/subscriptions/checkout', { plan_slug }),
}

export const promoApi = {
  activate: (code: string) => api.post('/promo/activate', { code }),
  history: () => api.get('/promo/history'),
}

/** Typed SSE events from /assistant/messages/stream */
export type AssistantStreamEvent =
  | {
      type: 'classification'
      skill: string
      used_fallback?: boolean
      fallback_reason?: string | null
      rag_chunks?: number
      estimated_prompt_chars?: number
    }
  | { type: 'content'; content: string }
  | {
      type: 'done'
      message_id?: string
      tokens_in?: number | null
      tokens_out?: number | null
      skill_used?: string
      used_fallback?: boolean
      fallback_reason?: string | null
      rag_chunks?: number
      done?: boolean
    }
  | { type: 'error'; error: string; subscription?: SubscriptionErrorDetail }

export function mapAssistantStreamError(status: number, body?: string): string {
  if (status === 401) return 'Session expired. Please sign in again.'
  if (status === 403) return 'You do not have access to this chat.'
  if (status === 404) return 'Chat not found.'
  if (status >= 500) return 'Server temporarily unavailable. Please try again later.'
  if (body) return body
  return `Connection error (${status}).`
}

/**
 * Stream assistant response via SSE.
 * Returns an async generator yielding parsed SSE events.
 */
export async function* sendMessageStream(
  chatId: string,
  content: string,
): AsyncGenerator<AssistantStreamEvent> {
  const response = await authenticatedFetch(`${API_URL}/assistant/messages/stream`, {
    method: 'POST',
    body: JSON.stringify({ chat_id: chatId, content }),
  })

  if (!response.ok) {
    let detail: unknown = ''
    try {
      const err = await response.json()
      detail = err?.detail ?? err?.error ?? ''
    } catch {
      detail = await response.text().catch(() => '')
    }
    if (
      response.status === 403
      && detail
      && typeof detail === 'object'
      && detail !== null
      && 'code' in detail
    ) {
      throw new SubscriptionAccessError(detail as SubscriptionErrorDetail)
    }
    const detailStr = typeof detail === 'string' ? detail : JSON.stringify(detail)
    throw new Error(mapAssistantStreamError(response.status, detailStr))
  }

  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6)) as AssistantStreamEvent
          yield data
        } catch {
          /* skip malformed chunk */
        }
      }
    }
  }
}
